import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RoadmapServiceService } from '@edtech-ui/core/services/roadmap-service/roadmap-service.service';
import {
  ApiRoadmap,
  ApiSubtopic,
  ApiSubtopicProgress,
  ApiTopicProgress,
  RoadmapProgressResponse,
  RoadmapAndProgressResponse,
} from '@edtech-ui/core/models/roadmap/roadmap';
import { ToastrService } from 'ngx-toastr';
import { RoadmapProgressStatus } from '@edtech-ui/constants/student-roadmap-ta.enum';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-roadmap-details',
  standalone: true,
  imports: [CommonModule, RouterModule, MatProgressSpinner, HasPermissionDirective],
  templateUrl: './roadmap-details.component.html',
  styleUrl: './roadmap-details.component.scss',
})
export class RoadmapDetailsComponent implements OnInit {

  RoadmapProgressStatus = RoadmapProgressStatus;
  PermissionForStudents = PermissionForStudents;
  roadmapId!: string;
  roadmap!: ApiRoadmap;
  openPhaseIndex: number | null = null;
  openSubTopic: string | null = null;
  hasStarted = false;
  isStarting = false;
  progressMap = new Map<string, string>();
  topicsProgress: ApiTopicProgress[] = [];
  overallProgress: number = 0;
  isLoading = true;
  fromTab!: string;
  totalCompletedSubtopics = 0;
  loadingTitle: string = '';
  isUpdating: boolean = false;
  currentTopicTitle: string = '';
  currentSubtopicTitle: string = '';
 topicStatsMap: Partial<Record<string, {
  status: string;
  progress: number;
  completedSteps: number;
  totalSteps: number;
}>> = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private roadmapService: RoadmapServiceService,
    private _toastr: ToastrService,
    private _authService: AuthServiceService,
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;

    this.roadmapId = id;
    this.fromTab = this.route.snapshot.queryParamMap.get('fromTab') || '';
    this.loadingTitle = this.cleanTitle(window.history.state?.title || '');

    if (!this._authService.hasPermission(PermissionForStudents.ROADMAP_VIEW)) return;

    this.loadRoadmapWithProgress();
  }

  loadRoadmapWithProgress(): void {
    this.isLoading = true;

    this.roadmapService.getRoadmapAndProgress(this.roadmapId).subscribe({
      next: (res: RoadmapAndProgressResponse) => {
        this.isLoading = false;

        if (!res.Success || !res.Data) {
          this._toastr.error(res.Message || 'Failed to load roadmap');
          return;
        }

        this.roadmap = {
          ...res.Data.roadmap,
          title: this.cleanTitle(res.Data.roadmap.title),
          topics: res.Data.roadmap.topics?.map((topic: any) => ({
            ...topic,
            subtopics: (topic.subtopics || topic.sub_topics || topic.subTopics || []).map((sub: any) => ({
              ...sub,
              subtopic_id: sub.subtopic_id || sub._id || sub.id
            }))
          })) || []
        };

        const progress = res.Data.progress;
        if (progress && progress.topics_progress?.length) {
          this.hasStarted = true;
          this.overallProgress = progress.overall_completion_percentage || 0;
          this.mapProgress(progress.topics_progress);
        } else {
          this.hasStarted = false;
          this.overallProgress = 0;
          this.progressMap.clear();
          this.topicsProgress = [];
          this.mapProgress([]);
        }
      },
      error: (err) => {
        this.isLoading = false;
        this._toastr.error(err?.error?.Message || 'Failed to load roadmap');
      }
    });
  }

  startLearning(event: Event, topicId: string, subtopic: ApiSubtopic): void {
    if (this.hasStarted) {
      this.toggleSubtopicStatus(event, topicId, subtopic);
      return;
    }
    if (this.isStarting) return;
    this.isStarting = true;
    this.roadmapService.startRoadmapProgress(this.roadmapId).subscribe({
      next: (res) => {
        this.hasStarted = true;
        this.isStarting = false;
        this._toastr.success(res.Message);
        this.toggleSubtopicStatus(event, topicId, subtopic);
      },
      error: (err) => {
        this.isStarting = false;
        this._toastr.error(err.error.Message);
      },
    });
  }

  mapProgress(topicsProgress: ApiTopicProgress[]): void {
    this.progressMap.clear();
    let completeCount = 0;
    this.topicsProgress = topicsProgress;
    this.topicStatsMap = {};

    topicsProgress.forEach((topic: ApiTopicProgress) => {
      topic.subtopics_progress?.forEach((sub: ApiSubtopicProgress) => {
        this.progressMap.set(sub.subtopic_id, sub.status);
        if (sub.status === RoadmapProgressStatus.DONE) {
          completeCount++;
        }
      });
    });

    this.totalCompletedSubtopics = completeCount;
    const topicMap = new Map<string, ApiTopicProgress>(
      topicsProgress.map(tp => [tp.topic_id, tp])
    );

    if (this.roadmap?.topics) {
      this.roadmap.topics.forEach(topic => {
        const tp = topicMap.get(topic.topic_id);
        const completedSteps = topic.subtopics.filter(sub => this.progressMap.get(sub.subtopic_id) === RoadmapProgressStatus.DONE).length;

        this.topicStatsMap[topic.topic_id] = {
          status: tp?.status || 'PENDING',
          progress: tp?.completion_percentage || 0,
          completedSteps: completedSteps,
          totalSteps: topic.subtopics.length
        };
      });
    }

    this.calculateCurrentTopicAndSubtopic();
  }

  calculateCurrentTopicAndSubtopic(): void {
    this.currentTopicTitle = '';
    this.currentSubtopicTitle = '';

    if (!this.roadmap?.topics) return;

    let pending: { topic: any, sub: any } | null = null;

    for (const topic of this.roadmap.topics) {
      for (const sub of topic.subtopics) {

        const status = this.progressMap.get(sub.subtopic_id);

        if (status === RoadmapProgressStatus.IN_PROGRESS) {
          this.currentTopicTitle = topic.title;
          this.currentSubtopicTitle = sub.title;
          return;
        }

        if (!pending && (!status || status === RoadmapProgressStatus.PENDING)) {
          pending = { topic, sub };
        }
      }
    }

    if (pending) {
      this.currentTopicTitle = pending.topic.title;
      this.currentSubtopicTitle = pending.sub.title;
    }
  }

  toggleSubtopic(sub: ApiSubtopic): void {
    if (this.openSubTopic === sub.subtopic_id) {
      this.openSubTopic = null;
      return;
    }
    this.openSubTopic = sub.subtopic_id;
    const topicIndex = this.roadmap.topics.findIndex(t =>
      t.subtopics.some(s => s.subtopic_id === sub.subtopic_id)
    );

    if (topicIndex !== -1) {
      this.openPhaseIndex = topicIndex;

      setTimeout(() => {
        const element = document.getElementById(`flush-heading-${topicIndex}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }
  toggleSubtopicStatus(event: Event, topicId: string, sub: ApiSubtopic): void {

    const currentStatus =
      this.progressMap.get(sub.subtopic_id) || RoadmapProgressStatus.PENDING;

    const nextStatus =
      currentStatus === RoadmapProgressStatus.PENDING
        ? RoadmapProgressStatus.IN_PROGRESS
        : currentStatus === RoadmapProgressStatus.IN_PROGRESS
          ? RoadmapProgressStatus.DONE
          : RoadmapProgressStatus.PENDING;

    this.updateSubtopicStatus(event, topicId, sub.subtopic_id, nextStatus);
  }
  updateSubtopicStatus(
    event: Event,
    topicId: string,
    subtopicId: string,
    status: RoadmapProgressStatus
  ): void {

    event.stopPropagation();

    if (this.isUpdating) return;

    this.isUpdating = true;

    this.roadmapService
      .updateSubtopicProgress(this.roadmapId, topicId, subtopicId, status)
      .pipe(finalize(() => this.isUpdating = false))
      .subscribe({
        next: (res: RoadmapProgressResponse) => {
          this.handleProgressUpdate(res);
        },
        error: (err) => {
          this._toastr.error(err?.error?.Message || 'Something went wrong');
          this.refreshProgress();
        }
      });
  }

  refreshProgress(): void {
    this.roadmapService.getRoadmapWithProgress(this.roadmapId).subscribe({
      next: (res: RoadmapProgressResponse) => this.handleProgressUpdate(res),
      error: (err) => this._toastr.error(err.error.Message)
    });
  }

  private handleProgressUpdate(res: RoadmapProgressResponse): void {
    if (res.Success && res.Data) {
      this.overallProgress = res.Data.overall_completion_percentage || 0;
      if (res.Data.topics_progress) {
        this.mapProgress(res.Data.topics_progress);
      }
    }
  }

  togglePhase(i: number): void {
    this.openPhaseIndex = this.openPhaseIndex === i ? null : i;

    if (this.openPhaseIndex === i) {
      setTimeout(() => {
        const element = document.getElementById(`flush-heading-${i}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }

  goBack(): void {
    this.router.navigate(
      ['/student-dashboard/my-roadmap'],
      { queryParams: { fromTab: this.fromTab } }
    );
  }

  resumeLastStep(): void {
    if (!this.roadmap?.topics) return;

    let targetTopicIndex = -1;
    let targetSubtopicId: string | null = null;

    for (let i = 0; i < this.roadmap.topics.length; i++) {
      const inProgressSub = this.roadmap.topics[i].subtopics.find(
        sub => this.progressMap.get(sub.subtopic_id) === RoadmapProgressStatus.IN_PROGRESS
      );
      if (inProgressSub) {
        targetTopicIndex = i;
        targetSubtopicId = inProgressSub.subtopic_id;
        break;
      }
    }

    if (targetTopicIndex === -1) {
      for (let i = 0; i < this.roadmap.topics.length; i++) {
        const pendingSub = this.roadmap.topics[i].subtopics.find(
          sub => !this.progressMap.has(sub.subtopic_id) ||
            this.progressMap.get(sub.subtopic_id) === RoadmapProgressStatus.PENDING
        );
        if (pendingSub) {
          targetTopicIndex = i;
          targetSubtopicId = pendingSub.subtopic_id;
          break;
        }
      }
    }

    if (targetTopicIndex !== -1 && targetSubtopicId) {
      this.openPhaseIndex = targetTopicIndex;
      this.openSubTopic = targetSubtopicId;

      setTimeout(() => {
        const element = document.getElementById(`flush-heading-${targetTopicIndex}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }

  hasRoadmapStarted(): boolean {
    return this.hasStarted || this.progressMap.size > 0;
  }



  markSubtopicComplete(event: Event, topicId: string, sub: ApiSubtopic) {
    this.updateSubtopicStatus(
      event,
      topicId,
      sub.subtopic_id,
      RoadmapProgressStatus.DONE
    );
  }

  resetSubtopic(event: Event, topicId: string, sub: ApiSubtopic) {
    this.updateSubtopicStatus(
      event,
      topicId,
      sub.subtopic_id,
      RoadmapProgressStatus.PENDING
    );
  }


  private cleanTitle(title: string): string {
    if (!title) return title;
    return title.replace(/\s*\(Fork\)$/i, '').trim();
  }
}
